import React, { useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import consignmentService, { type ConsignmentReturn } from '@/services/consignment.service';
import { useWarehouses } from '@/hooks/useInventory';

const { Title, Text } = Typography;

export const ConsignmentReturnsPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<string | undefined>();
  const [warehouseId, setWarehouseId] = React.useState<string | undefined>();
  const { data: warehouses = [] } = useWarehouses();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['consignment-returns', status, warehouseId],
    queryFn: () =>
      consignmentService.listReturns({
        ...(status ? { status } : {}),
        ...(warehouseId ? { warehouse_id: warehouseId } : {}),
      }),
  });

  const warehouseMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of warehouses as Array<{ id: string; warehouse_name?: string }>) {
      m.set(w.id, w.warehouse_name || w.id);
    }
    return m;
  }, [warehouses]);

  const columns: ColumnsType<ConsignmentReturn> = useMemo(
    () => [
      { title: 'Number', dataIndex: 'return_number', key: 'n', width: 160 },
      {
        title: 'Date',
        dataIndex: 'return_date',
        key: 'd',
        width: 120,
        render: (d: string) => (d ? String(d).slice(0, 10) : '—'),
      },
      {
        title: 'Warehouse',
        key: 'w',
        render: (_: unknown, r) => warehouseMap.get(r.warehouse_id) ?? r.warehouse_id,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 's',
        width: 120,
        render: (s: string) => {
          const color = s === 'Posted' ? 'green' : s === 'Cancelled' ? 'default' : 'gold';
          return <Tag color={color}>{s}</Tag>;
        },
      },
      {
        title: '',
        key: 'a',
        width: 100,
        render: (_: unknown, r) => (
          <Link to={`/scm/inventory/consignment-returns/${r.id}`}>Open</Link>
        ),
      },
    ],
    [warehouseMap]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Consignment returns
              </Title>
              <Text type="secondary">
                Return supplier-consigned stock OUT (ownership SUPPLIER_CONSIGNMENT).
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/scm/inventory/consignment-returns/new')}
            >
              New return
            </Button>
          </Space>
          <Space wrap>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={setStatus}
              options={[
                { value: 'Draft', label: 'Draft' },
                { value: 'Posted', label: 'Posted' },
                { value: 'Cancelled', label: 'Cancelled' },
              ]}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Warehouse"
              style={{ width: 240 }}
              value={warehouseId}
              onChange={setWarehouseId}
              options={(warehouses as Array<{ id: string; warehouse_name?: string }>).map((w) => ({
                value: w.id,
                label: w.warehouse_name || w.id,
              }))}
            />
          </Space>
          <Table<ConsignmentReturn>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ConsignmentReturnsPage;
